import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import Container from "@/components/Container";
import Eyebrow from "@/components/Eyebrow";
import Reveal from "@/components/Reveal";
import type { CaseStudy, Testimonial } from "@/lib/content";
import Section from "@/components/Section";

/**
 * Content-gated section.
 *
 * Case studies require verified metrics; testimonials require written
 * permission to publish name, title, and company (enforced in the database).
 * With no published rows of either kind, the section renders nothing at all —
 * never a skeleton, a placeholder, or "coming soon".
 */
function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function Results({
  caseStudies = [],
  testimonials = [],
}: {
  caseStudies?: CaseStudy[];
  testimonials?: Testimonial[];
}) {
  if (caseStudies.length === 0 && testimonials.length === 0) return null;

  return (
    <Section tone="paper">
      <Container>
        <Reveal className="max-w-[52ch]">
          <Eyebrow>Results</Eyebrow>
          <h2 className="mt-3 font-display text-h2 text-ink-900">The work, and what it did.</h2>
          <p className="mt-4 text-body-lg text-ink-600">Real engagements, real outcomes.</p>
        </Reveal>

        {caseStudies.length > 0 && (
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {caseStudies.map((study, i) => (
              <Reveal key={study.id} delay={(i % 3) * 60} className="h-full">
                <article className="flex h-full flex-col rounded-xl border border-ink-200 bg-paper-0 p-7">
                  {study.industry && (
                    <span className="inline-flex w-fit rounded-full border border-ink-200 px-3 py-1 font-mono text-eyebrow uppercase font-medium text-ink-500">
                      {study.industry}
                    </span>
                  )}

                  <h3 className="mt-5 font-display text-h3 text-ink-900">
                    {study.outcome_headline}
                  </h3>

                  {study.summary && <p className="mt-3 text-body text-ink-600">{study.summary}</p>}

                  {(study.metric_1_value || study.metric_2_value) && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {study.metric_1_value && (
                        <span className="tabular rounded-md bg-brand-50 px-3 py-1.5 text-body-sm text-brand-700">
                          {study.metric_1_value}
                          {study.metric_1_label ? ` ${study.metric_1_label}` : ""}
                        </span>
                      )}
                      {study.metric_2_value && (
                        <span className="tabular rounded-md bg-brand-50 px-3 py-1.5 text-body-sm text-brand-700">
                          {study.metric_2_value}
                          {study.metric_2_label ? ` ${study.metric_2_label}` : ""}
                        </span>
                      )}
                    </div>
                  )}

                  <Link
                    to="/results/$slug"
                    params={{ slug: study.slug }}
                    className="group mt-auto inline-flex w-fit items-center gap-2 pt-6 text-body-sm font-medium text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
                  >
                    Read the full story
                    <ArrowRight
                      size={20}
                      strokeWidth={1.5}
                      className="transition-transform duration-150 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {testimonials.length > 0 && (
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={(i % 3) * 60} className="h-full">
                <figure className="flex h-full flex-col rounded-xl border border-ink-200 bg-paper-0 p-7">
                  <blockquote className="text-body text-ink-600">{t.quote}</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="flex size-10 shrink-0 items-center justify-center rounded-full bg-ink-100 font-mono text-body-sm text-ink-500"
                    >
                      {initials(t.author_name)}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-body-sm font-medium text-ink-900">
                        {t.author_name}
                      </span>
                      {(t.author_title || t.company) && (
                        <span className="block text-body-sm text-ink-500">
                          {[t.author_title, t.company].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}
      </Container>
    </Section>
  );
}

export default Results;
